import { HiEnvelope } from "react-icons/hi2";
import { Progress } from "flowbite-react";

const REASON_MESSAGES = {
  quota_exhausted: "Daily AI quota reached. Extraction will resume tomorrow.",
  rate_limit:
    "AI analysis quota exceeded. Will retry automatically in a few minutes.",
  service_error:
    "AI analysis service is temporarily unavailable. Will retry shortly.",
  all_failed: "Email analysis encountered errors. Will retry automatically.",
};

const STATUS_CONFIG = {
  extracting: {
    label: (extracted, total, days) =>
      `Analyzing emails from the last ${days} days… ${extracted}/${total} complete`,
    sublabel: () => "AI insights and signals will appear when complete.",
    showProgress: true,
  },
  processing: {
    label: (extracted) => `Generating signals from ${extracted} emails…`,
    sublabel: () => "Almost done — detecting patterns and creating alerts.",
    showProgress: false,
  },
  pending: {
    label: () => "Preparing email analysis…",
    sublabel: (days) =>
      `Checking recent emails (last ${days} days) for this account.`,
    showProgress: false,
  },
  loading: {
    label: () => "Checking email analysis status…",
    sublabel: () => null,
    showProgress: false,
  },
  cooldown: {
    label: () => "Email analysis paused",
    sublabel: () => null, // Overridden by reason
    showProgress: false,
  },
  error: {
    label: () => "Email analysis is taking longer than expected.",
    sublabel: () =>
      "It's still processing — progress will update automatically.",
    showProgress: false,
  },
};

export const ExtractionProgressCard = ({
  total,
  extracted,
  status,
  reason,
  lookbackDays = 90,
}) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.loading;
  const percent = total > 0 ? Math.round((extracted / total) * 100) : 0;

  // Use reason-specific message for cooldown, fallback to config sublabel
  const sublabel =
    status === "cooldown" && reason
      ? REASON_MESSAGES[reason] || REASON_MESSAGES.all_failed
      : config.sublabel(lookbackDays);

  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <HiEnvelope className="size-4 text-blue-500" />
        <h2 className="text-base font-semibold text-gray-900">
          Email Analysis
        </h2>
      </div>
      <div className="rounded-xl border border-gray-200 bg-white px-5 py-4">
        <p className="text-sm font-medium text-gray-700">
          {config.label(extracted, total, lookbackDays)}
        </p>
        {config.showProgress && total > 0 && (
          <div className="mt-3">
            <Progress progress={percent} size="md" color="blue" />
          </div>
        )}
        {sublabel && <p className="mt-2 text-xs text-gray-500">{sublabel}</p>}
      </div>
    </div>
  );
};

/**
 * Compact source badge — shown when extraction is complete.
 */
export const EmailCountBadge = ({ extracted, lookbackDays = 90 }) => {
  if (!extracted) return null;
  return (
    <span className="inline-flex items-center gap-1 text-xs text-gray-500">
      <HiEnvelope className="size-3" />
      Based on emails, usage data, revenue signals, and more
    </span>
  );
};
